import { inject, injectable } from 'inversify'
import { pick } from 'lodash-es'
import { nanoid } from 'nanoid/non-secure'
import { Float32BufferAttribute, Group, type Line, Vector2 } from 'three'
import type { Line2 } from 'three/examples/jsm/lines/Line2.js'
import { setup } from 'xstate'
import { Setting } from '@/plugins/cad/module/common/setting'
import { TYPES } from '@/plugins/cad/module/container/types'
import type { KeyEventParams, MouseEventParams } from '@/plugins/cad/module/core/common/event'
import { StateMachineTool } from '@/plugins/cad/module/core/tool/StateMachineTool'
import { CadTipKey } from '@/plugins/cad/module/i18n/tipKeys'
import LineDimensionGroup from '@/plugins/cad/module/objects/LineDimensionGroup'
import { DrawUtils } from '@/plugins/cad/module/utils/DrawUtils'
import type { UnitPresentationService } from '@/runtime/UnitPresentationService'
import { KeyCodes } from '@/shared/constants'
import type { AuxiliaryMouseHelper } from '../auxiliaryTool/auxiliaryMouseHelper'
import { type MouseTipHelper, MouseTipId } from '../auxiliaryTool/MouseTipHelper'
import type { CADController } from '../CADController'
import { DrawEntityCommand } from '../command/DrawEntityCommand'
import type { CADViewManager } from '../manager/CADViewManager'
import type { HistoryManager } from '../manager/HistoryManager'
import type { IPoint } from '../model/common/interface'
import { EllipseEntity } from '../model/entity'
import { createCadLine2Object } from '../object/material/CadLineFactory'
import { MaterialUtils } from '../object/material/MaterialUtils'
import type { EntitySelector } from '../selector/entitySelector'

const EllipseMouseTip = {
  axisEnd: CadTipKey.draw.ellipse.axisEnd,
  otherAxisEnd: CadTipKey.draw.ellipse.otherAxisEnd,
  otherSemiAxisLength: CadTipKey.draw.ellipse.otherSemiAxisLength,
}

interface IEllipseParams {
  center: IPoint
  majorAxisEndPoint: IPoint
  axisRatio: number
  radiusX: number
  radiusY: number
  rotation: number
  axisPoint: IPoint
}

const ellipseMachine = setup({
  actions: {
    showMouseTip: (..._args: any) => {},
    entryFinished: () => {},
  },
}).createMachine({
  id: 'drawEllipseTool',
  initial: 'idle',
  states: {
    idle: {
      on: {
        NEXT: [{ target: 'draw.threePoints' }],
      },
    },
    draw: {
      initial: 'threePoints',
      states: {
        // 模式一：三点椭圆
        threePoints: {
          initial: 'point1',
          states: {
            point1: {
              on: { NEXT: 'point2' },
              entry: { type: 'showMouseTip', params: EllipseMouseTip.axisEnd },
            },
            point2: {
              on: { NEXT: 'point3' },
              entry: { type: 'showMouseTip', params: EllipseMouseTip.otherAxisEnd },
            },
            point3: {
              on: { NEXT: '#drawEllipseTool.finished', PREVIOUS: 'point2' },
              entry: { type: 'showMouseTip', params: EllipseMouseTip.otherSemiAxisLength },
            },
          },
        },
      },
    },
    finished: { entry: 'entryFinished', type: 'final' },
  },
})

/**
 * 椭圆绘制工具，依次拾取中心、长轴端点与短轴端点。
 */
@injectable()
export class DrawEllipseTool extends StateMachineTool<typeof ellipseMachine> {
  /** 工具名，等同于容器注册的符号 */
  name = TYPES.DrawEllipseTool

  @inject(TYPES.CADViewManager)
  private readonly viewManager!: CADViewManager

  @inject(TYPES.EntitySelector)
  private readonly entitySelector!: EntitySelector

  @inject(TYPES.MouseTipHelper)
  private readonly mouseTipHelper!: MouseTipHelper

  @inject(TYPES.UnitPresentationService)
  private readonly unitPresentation!: UnitPresentationService

  @inject(TYPES.HistoryManager)
  private readonly historyManager!: HistoryManager

  @inject(TYPES.AuxiliaryMouseHelper)
  private readonly auxiliaryMouseHelper!: AuxiliaryMouseHelper

  protected machine = ellipseMachine.provide({
    actions: {
      showMouseTip: (_v, tip) => {
        this.showMouseTip(tip)
      },
      entryFinished: () => {
        this.updateEntity()
        this.exit()
      },
    },
  })

  /** 所属控制器，由框架自动注入 */
  declare controller: CADController
  private points: IPoint[] = []
  private tempPoint: IPoint | null = null
  private group = new Group()
  private ellipseObject: Line2 | null = null
  private dimensionGroup: LineDimensionGroup | null = null
  private previewLineObject: Line | null = null
  private entity: EllipseEntity | null = null
  private currentCommand: DrawEntityCommand | null = null
  private ellipseParams: IEllipseParams | null = null

  protected onInitialize(entityData?: Partial<EllipseEntity>): void {
    this.viewManager.attachTempObject(this.group)
    this.entitySelector.clearSelection()
    this.inject(this.auxiliaryMouseHelper)

    this.entity = this.createEntity(entityData)
    this.currentCommand = new DrawEntityCommand(
      this.entity,
      this.viewManager,
      this.handleUndo.bind(this),
      this.isCanCommit.bind(this),
      this.isCanUndoStep.bind(this)
    )
    this.historyManager.startCommand(this.currentCommand)
    this.send({ type: 'NEXT' })
  }

  protected onTerminate(): void {
    this.historyManager.commitCommand()
    this.ellipseObject && DrawUtils.disposeObject(this.ellipseObject)
    this.dimensionGroup?.dispose()
    this.previewLineObject && DrawUtils.disposeObject(this.previewLineObject)
    this.group.parent?.remove(this.group)
    this.mouseTipHelper.removeTip(MouseTipId.Draw)
    this.eject(this.auxiliaryMouseHelper)

    this.points = []
    this.tempPoint = null
    this.ellipseObject = null
    this.dimensionGroup = null
    this.previewLineObject = null
    this.entity = null
    this.currentCommand = null
    this.ellipseParams = null
  }

  /**
   * 鼠标按下：左键推进状态机并记录一个点。
   *
   * @param params - 鼠标事件参数
   */
  /************ 鼠标键盘事件处理 ************/
  onMouseDown(params: MouseEventParams) {
    if (params.event.button === 0) {
      // 左键
      if (this.tempPoint) {
        this.points.push({ x: this.tempPoint.x, y: this.tempPoint.y })
        this.tempPoint = null
        this.send({ type: 'NEXT' })
        this.updateCanvas()
        this.historyManager.refresh()
      }
    } else if (params.event.button === 2) {
      // 右键
    }
  }

  /**
   * 鼠标移动：更新椭圆预览点。
   *
   * @param params - 鼠标事件参数
   */
  onMouseMove(params: MouseEventParams) {
    this.tempPoint = { x: params.worldPos.x, y: params.worldPos.y }
    this.mouseCapture(params)
    this.updateCanvas()
  }

  /**
   * 键盘按下：ESC 退出工具。
   *
   * @param params - 键盘事件参数
   */
  onKeyDown(params: KeyEventParams) {
    if (params.event.keyCode === KeyCodes.ESC) {
      this.exit()
    }
  }

  /** 每帧刷新椭圆标注 */
  onViewerUpdate() {
    this.dimensionGroup?.update()
  }

  // 正交捕捉处理
  private mouseCapture(params: MouseEventParams) {
    if (!this.tempPoint) {
      return
    }
    const startPoint = this.points[this.points.length - 1]
    const finalPoint = this.auxiliaryMouseHelper.mouseCapture(params, { startPoint })
    this.tempPoint = { x: finalPoint.x, y: finalPoint.y }
  }

  /************ 状态机状态处理 ************/
  private showMouseTip(tip: string) {
    this.mouseTipHelper.addTip(MouseTipId.Draw, tip)
  }

  /************ 渲染 ************/
  private updateCanvas() {
    this.updateObject()
  }

  private updateObject() {
    const points = [...this.points]
    if (this.tempPoint) {
      points.push(this.tempPoint)
    }
    if (points.length < 2) {
      return
    }

    // 计算圆弧参数
    const ellipseParams = this.getEllipseParams(points)
    this.updateEllipseObject(ellipseParams)
    this.ellipseParams = ellipseParams

    const p1 = ellipseParams ? ellipseParams.center : points[points.length - 2]
    const p2 = points[points.length - 1]
    // 绘制辅助预览线
    this.updatePreviewLine(p1, p2)
    // 绘制标注
    this.updateDimensionGroup(p1, ellipseParams ? ellipseParams.axisPoint : p2, !ellipseParams)
  }

  private updatePreviewLine(p1: IPoint, p2: IPoint) {
    if (!this.previewLineObject) {
      const dashSize = this.controller.getPixelSizeInWorld(20)
      const materialOption = {
        color: Setting.previewLineConfig.color,
        transparent: Setting.previewLineConfig.opacity < 1,
        opacity: Setting.previewLineConfig.opacity,
        dashSize,
        gapSize: dashSize / 2,
        depthTest: false,
        depthWrite: false,
      }
      this.previewLineObject = DrawUtils.drawDashedLine(p1, p2, materialOption, this.group)
      return
    }

    const positions = [p1, p2].flatMap((p) => [p.x, p.y, 0])
    const { geometry } = this.previewLineObject
    geometry.setAttribute('position', new Float32BufferAttribute(positions, 3))
    geometry.attributes.position.needsUpdate = true
    this.previewLineObject.computeLineDistances()
  }

  private updateDimensionGroup(startPoint: IPoint, endPoint: IPoint, showAngle = true) {
    if (this.dimensionGroup) {
      this.dimensionGroup.updateOptions({ showAngle, lengthOffset: showAngle ? undefined : 0 })
      this.dimensionGroup.updateLine(startPoint, endPoint)
      return
    }
    this.dimensionGroup = new LineDimensionGroup(
      startPoint,
      endPoint,
      {},
      this.group,
      this.controller.domElement!.parentElement!,
      (point: IPoint) => this.controller.getScreenPosition(point),
      (radius: number) => this.controller.getPixelSizeInWorld(radius),
      this.unitPresentation
    )
  }

  private updateEllipseObject(params: IEllipseParams | null) {
    if (!params || !this.entity) {
      this.ellipseObject && (this.ellipseObject.visible = false)
      return
    }
    const options = {
      center: { x: 0, y: 0 },
      ...pick(params, ['radiusX', 'radiusY', 'rotation']),
    }
    const points = DrawUtils.getEllipsePoints(options)
    if (!this.ellipseObject) {
      const material = MaterialUtils.createMaterialByLineType(
        this.entity.lineType,
        MaterialUtils.getMaterialOptionByEntity(this.entity)
      )
      const geometry = DrawUtils.getLineGeometryByPoints(points)
      this.ellipseObject = createCadLine2Object(geometry, material)
      this.ellipseObject.position.set(params.center.x, params.center.y, 0)
      this.ellipseObject.computeLineDistances()
      this.group.add(this.ellipseObject)
      return
    }
    this.ellipseObject.geometry.setPositions(points.flatMap((p) => [p.x, p.y, 0]))
    this.ellipseObject.geometry.attributes.position.needsUpdate = true
    this.ellipseObject.position.set(params.center.x, params.center.y, 0)
    this.ellipseObject.computeLineDistances()
    this.ellipseObject.visible = true
  }

  /************ 数据创建 ************/
  private createEntity(entityData?: Partial<EllipseEntity>) {
    const entity = new EllipseEntity({
      ...this.viewManager.getEntityDefaultData(entityData),
      id: nanoid(),
    })
    return entity
  }

  private updateEntity() {
    if (this.entity && this.ellipseParams) {
      Object.assign(
        this.entity,
        pick(this.ellipseParams, ['center', 'majorAxisEndPoint', 'axisRatio'])
      )
    }
  }

  private handleUndo() {
    if (this.isCanUndoStep()) {
      this.points.pop()
      this.updateCanvas()
      this.send({ type: 'PREVIOUS' })
    }
  }

  private isCanCommit(): boolean {
    return this.entity ? !!this.entity.center : false
  }

  private isCanUndoStep(): boolean {
    return this.points.length > 1
  }

  private getEllipseParams(points: IPoint[]): IEllipseParams | null {
    if (points.length === 3) {
      return this.getEllipseParamsByThreePoints(points[0], points[1], points[2])
    }
    return null
  }

  private getEllipseParamsByThreePoints(p1: IPoint, p2: IPoint, p3: IPoint): IEllipseParams | null {
    // 1. 中心点
    const center = {
      x: (p1.x + p2.x) / 2,
      y: (p1.y + p2.y) / 2,
    }

    // 2. 当前定义的长半轴向量和长度 a
    const majorX = p1.x - center.x
    const majorY = p1.y - center.y
    const a = Math.hypot(majorX, majorY)
    if (a === 0) {
      // 如果两点重合，无法定义长轴
      return null
    }

    // 3. 计算短半轴 b (p3到中心点的距离)
    const b = Math.hypot(p3.x - center.x, p3.y - center.y)
    if (b === 0) {
      // 如果第三点与中心重合，无法定义短轴
      return null
    }

    // 4. 自动修正：若 b > a，则交换长短轴定义
    let majorAxisEndPoint: IPoint
    let axisRatio: number

    if (b > a) {
      // 真正的长轴是原来的短轴方向
      // 将原长轴向量旋转 90 度，并缩放到长度 b
      const ux = majorX / a // 长轴单位向量 x 分量
      const uy = majorY / a // 长轴单位向量 y 分量
      majorAxisEndPoint = {
        x: -uy * b, // 逆时针旋转 90 度: (-y, x)
        y: ux * b,
      }
      axisRatio = a / b // 比例变为 a:b
    } else {
      // 原定义正确
      majorAxisEndPoint = { x: majorX, y: majorY }
      axisRatio = b / a
    }

    const axisPoint = new Vector2(center.x, center.y).addScaledVector(
      new Vector2(majorX, majorY).rotateAround({ x: 0, y: 0 }, -Math.PI / 2).normalize(),
      b
    )

    return {
      center,
      majorAxisEndPoint,
      axisRatio,
      radiusX: Math.max(a, b),
      radiusY: Math.min(a, b),
      rotation: Math.atan2(majorAxisEndPoint.y, majorAxisEndPoint.x),
      axisPoint,
    }
  }
}
